import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  NativeSelect,
  Textarea,
  useToast,
} from '@provenance/design';
import { MasterDataListType, type MasterDataItem } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';

function useMasterData(listType: MasterDataListType) {
  return useQuery({
    queryKey: ['master-data', listType],
    queryFn: () => api.get<MasterDataItem[]>(`/api/console/master-data/${listType}`),
    staleTime: 5 * 60_000,
  });
}

function useAfterDecision(registrationId: string, close: () => void) {
  const queryClient = useQueryClient();
  return (announce: () => void) => {
    void queryClient.invalidateQueries({ queryKey: ['registration', registrationId] });
    void queryClient.invalidateQueries({ queryKey: ['registrations'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    announce();
    close();
  };
}

/**
 * APPROVE — the only action in the product that creates an account.
 *
 * The dialog is explicit about that, because it is the consequence an operator most
 * needs to understand before pressing it: an organisation comes into existence and a
 * person is emailed a link that lets them into this chamber's data.
 */
export function ApproveRegistrationDialog({
  registration,
  open,
  onOpenChange,
}: {
  registration: { id: string; companyName: string; contactEmail: string; contactName: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const finish = useAfterDecision(registration.id, () => onOpenChange(false));
  const [note, setNote] = useState('');

  const approve = useMutation({
    mutationFn: () =>
      api.post(`/api/console/registrations/${registration.id}/approve`, { note: note.trim() || null }),
    onSuccess: () =>
      finish(() =>
        toast.toast({
          tone: 'success',
          title: `${registration.companyName} is registered`,
          description: `${registration.contactEmail} has been emailed a link to set their password.`,
        }),
      ),
    onError: (error) =>
      toast.error(
        'Could not approve this registration',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader
          eyebrow={registration.companyName}
          title="Approve this registration"
          description="This creates the company and its first user account in your chamber."
        />
        <DialogBody className="flex flex-col gap-4">
          <Callout tone="info" icon={<UserCheck className="size-4" />} title="What this does">
            <ul className="m-0 list-disc pl-4">
              <li>Creates {registration.companyName} as an organisation in your chamber.</li>
              <li>
                Creates an account for {registration.contactName} ({registration.contactEmail}).
              </li>
              <li>
                Emails them a single-use link to choose their own password. You never set or see
                it.
              </li>
            </ul>
          </Callout>

          <Field label="Note" help="Optional. Recorded in the audit trail; not sent to the applicant.">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="ABN verified against the ABR; known to us through the Brisbane trade office."
            />
          </Field>
        </DialogBody>
        <DialogFooter note="They can lodge certificate requests as soon as they set a password.">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={approve.isPending} onClick={() => approve.mutate()}>
            Approve and invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** REJECT — structured reason, same pattern as document rejection (spec §13.1). */
export function RejectRegistrationDialog({
  registration,
  open,
  onOpenChange,
}: {
  registration: { id: string; companyName: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const finish = useAfterDecision(registration.id, () => onOpenChange(false));
  const reasons = useMasterData(MasterDataListType.REGISTRATION_REJECTION_REASON);

  const [reasonId, setReasonId] = useState('');
  const [freeText, setFreeText] = useState('');
  const isOther = reasonId === 'OTHER';
  const canSubmit = (reasonId && !isOther) || (isOther && freeText.trim().length > 0);

  const reject = useMutation({
    mutationFn: () =>
      api.post(`/api/console/registrations/${registration.id}/reject`, {
        rejectionReasonId: isOther ? null : reasonId,
        freeText: freeText.trim() || null,
      }),
    onSuccess: () =>
      finish(() =>
        toast.toast({
          tone: 'success',
          title: 'Registration rejected',
          description: 'The applicant has been emailed the reason.',
        }),
      ),
    onError: (error) =>
      toast.error('Could not reject', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader
          eyebrow={registration.companyName}
          title="Reject this registration"
          description="No account is created. The applicant is emailed the reason and can apply again."
        />
        <DialogBody className="flex flex-col gap-4">
          <Callout tone="info" icon={<Info className="size-4" />}>
            If the problem is fixable — an unverifiable ABN, a missing document — <strong>Request
            information</strong> keeps the application open instead of making them start again.
          </Callout>

          <Field label="Reason" required help="From your chamber's managed list.">
            <NativeSelect value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
              <option value="">Choose a reason…</option>
              {(reasons.data ?? []).map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.label}
                </option>
              ))}
              <option value="OTHER">Other — describe it below</option>
            </NativeSelect>
          </Field>

          <Field label={isOther ? 'What is wrong' : 'Anything to add'} required={isOther}>
            <Textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              rows={3}
              placeholder="Included in the email to the applicant."
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!canSubmit} loading={reject.isPending} onClick={() => reject.mutate()}>
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * REQUEST INFORMATION — the loop that keeps a fixable application alive.
 *
 * The checklist is the SAME chamber-scoped list the document clarification flow uses
 * (spec §13.2), so a chamber maintains one list rather than two that drift apart.
 */
export function RequestInformationDialog({
  registration,
  open,
  onOpenChange,
}: {
  registration: { id: string; companyName: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const finish = useAfterDecision(registration.id, () => onOpenChange(false));
  const items = useMasterData(MasterDataListType.CLARIFICATION_ITEM);

  const [selected, setSelected] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');
  const [respondByDays, setRespondByDays] = useState(14);
  const canSubmit = selected.length > 0 || freeText.trim().length > 0;

  const request = useMutation({
    mutationFn: () =>
      api.post(`/api/console/registrations/${registration.id}/request-information`, {
        itemIds: selected,
        freeText: freeText.trim() || null,
        respondByDays,
      }),
    onSuccess: () =>
      finish(() =>
        toast.toast({
          tone: 'success',
          title: 'Information requested',
          description: `${registration.companyName} has a secure link to respond. It returns to your queue when they do.`,
        }),
      ),
    onError: (error) =>
      toast.error('Could not send the request', error instanceof ApiError ? error.message : ''),
  });

  const grouped = (items.data ?? []).reduce<Record<string, MasterDataItem[]>>((acc, item) => {
    (acc[item.category ?? 'Other'] ??= []).push(item);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow={registration.companyName}
          title="Ask for more before deciding"
          description="The application is not rejected. It waits for the applicant and comes back to your queue."
        />
        <DialogBody className="flex flex-col gap-4">
          <fieldset className="m-0 border-0 p-0">
            <legend className="[font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]">
              What do you need from them?
            </legend>
            <div className="mt-2.5 flex flex-col gap-3.5">
              {Object.entries(grouped).map(([category, categoryItems]) => (
                <div key={category}>
                  <p className="pv-eyebrow mb-1.5 text-[var(--pv-faint)]">{category}</p>
                  <div className="flex flex-col gap-2">
                    {categoryItems.map((item) => (
                      <Checkbox
                        key={item.id}
                        checked={selected.includes(item.id)}
                        onCheckedChange={(checked) =>
                          setSelected((prev) =>
                            checked === true ? [...prev, item.id] : prev.filter((id) => id !== item.id),
                          )
                        }
                        label={item.label}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </fieldset>

          <Field
            label="Anything specific"
            help="Included in the email. Be concrete — the applicant cannot see your screen."
          >
            <Textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              rows={3}
              placeholder="The ABN you gave is registered to a different trading name. Please confirm which entity is exporting."
            />
          </Field>

          <Field label="Respond within">
            <NativeSelect value={respondByDays} onChange={(e) => setRespondByDays(Number(e.target.value))}>
              {[7, 14, 21, 30].map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </NativeSelect>
          </Field>
        </DialogBody>
        <DialogFooter note="The link works without an account — they do not have one yet.">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} loading={request.isPending} onClick={() => request.mutate()}>
            Send the request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
